import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { adminApi, adminKeys } from '@/features/admin/api/admin.api';
import type { AdminGuideListItem, AdminGuideStatusGroup } from '@/features/admin/api/admin.api';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { DeleteAccountDialog } from '@/features/admin/components/DeleteAccountDialog';
import { GuideDirectoryList } from '@/features/admin/components/guide-directory/GuideDirectoryList';
import { GuideDirectoryStats } from '@/features/admin/components/guide-directory/GuideDirectoryStats';
import { GuideDirectoryToolbar } from '@/features/admin/components/guide-directory/GuideDirectoryToolbar';
import {
  DEFAULT_SORT,
  SORT_OPTIONS,
  guideDisplayName,
  isSortKey,
  isStatusGroup,
  type GuideSortKey,
} from '@/features/admin/components/guide-directory/guideDirectory.utils';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { GuideStatus } from '@/types';

const PAGE_SIZES = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 20;

function readPageSize(value: string | null): number {
  const parsed = Number(value);
  return (PAGE_SIZES as readonly number[]).includes(parsed) ? parsed : DEFAULT_PAGE_SIZE;
}

export function AdminGuidesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL so Back from a guide profile, a refresh or a shared
  // link all land on the same filtered view.
  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '');
  const search = useDebouncedValue(searchInput.trim(), 350);
  const [statusGroup, setStatusGroup] = useState<AdminGuideStatusGroup | ''>(() => {
    const value = searchParams.get('status');
    return isStatusGroup(value) ? value : '';
  });
  const [sort, setSort] = useState<GuideSortKey>(() => {
    const value = searchParams.get('sort');
    return isSortKey(value) ? value : DEFAULT_SORT;
  });
  const [page, setPage] = useState(() => Math.max(0, Number(searchParams.get('page') ?? 1) - 1 || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() => readPageSize(searchParams.get('size')));

  const [pendingDelete, setPendingDelete] = useState<AdminGuideListItem | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // `replace`, so debounced keystrokes don't pile up as history entries.
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set('q', search);
    if (statusGroup) next.set('status', statusGroup);
    if (sort !== DEFAULT_SORT) next.set('sort', sort);
    if (page > 0) next.set('page', String(page + 1));
    if (rowsPerPage !== DEFAULT_PAGE_SIZE) next.set('size', String(rowsPerPage));
    setSearchParams(next, { replace: true });
  }, [search, statusGroup, sort, page, rowsPerPage, setSearchParams]);

  // Skip the first run so a restored ?page= survives the initial mount.
  const [filtersKey, setFiltersKey] = useState(() => `${search}|${statusGroup}|${sort}`);
  useEffect(() => {
    const key = `${search}|${statusGroup}|${sort}`;
    if (key !== filtersKey) {
      setFiltersKey(key);
      setPage(0);
    }
  }, [search, statusGroup, sort, filtersKey]);

  const { data: stats } = useQuery({
    queryKey: adminKeys.dashboard(),
    queryFn: adminApi.getDashboard,
  });

  const params = {
    page: page + 1,
    limit: rowsPerPage,
    sortBy: SORT_OPTIONS[sort].sortBy,
    sortOrder: SORT_OPTIONS[sort].sortOrder,
    // Spread rather than `key: value || undefined` — react-query hashes an
    // explicit `undefined` differently from an absent key.
    ...(statusGroup ? { statusGroup } : {}),
    ...(search ? { search } : {}),
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: adminKeys.guides(params),
    queryFn: () => adminApi.listGuides(params),
    placeholderData: keepPreviousData,
  });

  // Server-side switch (ADMIN_DELETE_ENABLED). Absent/false hides the action.
  const canDelete = stats?.capabilities?.canDeleteAccounts === true;

  const deleteMutation = useMutation({
    mutationFn: (profileId: string) => adminApi.deleteGuide(profileId),
    onSuccess: () => {
      setPendingDelete(null);
      // Removing the last row of a trailing page would leave an empty table.
      if (data?.data?.length === 1 && page > 0) setPage((current) => current - 1);
      // A deleted guide also leaves the review queues and the counters.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });

  const deleteErrorMessage = deleteMutation.isError
    ? ((deleteMutation.error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? 'Failed to delete this guide. Please try again.')
    : null;

  const backTarget = `${location.pathname}${location.search}`;
  const openReview = (guide: AdminGuideListItem) =>
    navigate(ADMIN_ROUTES.review(guide.id), { state: { from: backTarget } });
  const openIdentityCard = (guide: AdminGuideListItem) =>
    navigate(ADMIN_ROUTES.identityCard(guide.id), { state: { from: backTarget } });

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setToast(`${label} copied`);
    } catch {
      setToast(`Couldn't copy ${label.toLowerCase()}`);
    }
  };

  const clearAll = () => {
    setSearchInput('');
    setStatusGroup('');
    setSort(DEFAULT_SORT);
  };

  const approvedCount = stats ? (stats.guidesByStatus?.[GuideStatus.APPROVED] ?? 0) : undefined;
  const pendingCount = stats
    ? (stats.guidesByStatus?.[GuideStatus.SUBMITTED] ?? 0) + (stats.guidesByStatus?.[GuideStatus.UNDER_REVIEW] ?? 0)
    : undefined;

  const isFiltered = Boolean(search || statusGroup);
  const guides = data?.data ?? [];

  const emptyState = (
    <Box sx={{ py: { xs: 6, md: 8 }, px: 2, textAlign: 'center' }}>
      <Box
        aria-hidden
        sx={{
          width: 48,
          height: 48,
          mx: 'auto',
          mb: 1.5,
          borderRadius: '50%',
          bgcolor: 'var(--border-light)',
          color: 'text.secondary',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <PersonSearchOutlinedIcon />
      </Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {isFiltered ? 'No guides match your filters' : 'No guide profiles yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {isFiltered
          ? 'Try a different name, Guide ID, email or phone number, or clear the filters.'
          : 'Guides appear here as soon as they register.'}
      </Typography>
      {isFiltered && (
        <Button variant="outlined" size="small" onClick={clearAll} sx={{ mt: 2 }}>
          Clear filters
        </Button>
      )}
    </Box>
  );

  return (
    <Box>
      <AdminPageHeader
        title="All Guides"
        subtitle="Manage and monitor all registered guides across the platform."
      />

      <GuideDirectoryStats
        total={stats?.totalGuideProfiles}
        approved={approvedCount}
        pending={pendingCount}
        statusGroup={statusGroup}
        onSelect={setStatusGroup}
      />

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <GuideDirectoryToolbar
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          appliedSearch={search}
          statusGroup={statusGroup}
          onStatusGroupChange={setStatusGroup}
          sort={sort}
          onSortChange={setSort}
          onClearAll={clearAll}
          total={data?.pagination?.total}
          isFetching={isFetching && !isLoading}
        />

        {isError && !data ? (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load guides. Please try again.
          </Alert>
        ) : (
          <Box sx={{ opacity: isFetching && !isLoading ? 0.6 : 1, transition: 'opacity .15s ease' }}>
            <GuideDirectoryList
              guides={guides}
              isLoading={isLoading}
              skeletonRows={Math.min(rowsPerPage, 8)}
              emptyState={emptyState}
              canDelete={canDelete}
              onView={openReview}
              onOpenIdentityCard={openIdentityCard}
              onCopy={copyToClipboard}
              onDelete={(guide) => {
                deleteMutation.reset();
                setPendingDelete(guide);
              }}
            />
          </Box>
        )}

        {(data?.pagination?.total ?? 0) > 0 && (
          <TablePagination
            component="div"
            count={data?.pagination?.total ?? 0}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(readPageSize(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[...PAGE_SIZES]}
            labelRowsPerPage="Rows"
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': { minHeight: 52 },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
            }}
          />
        )}
      </Paper>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2200}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <DeleteAccountDialog
        open={Boolean(pendingDelete)}
        subjectKind="guide"
        subjectName={pendingDelete ? guideDisplayName(pendingDelete) : ''}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteErrorMessage}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
      />
    </Box>
  );
}
